import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '#app/utils/misc.tsx'

/**
 * Alert / Callout: a form- or page-level message in one of four tones, each on
 * its own tinted surface so the tones read apart at a glance:
 *   info    → `--info-surface` / `--info-border`, title in `--info-text`
 *             (derived from brand-3),
 *   success → `--success-surface` / `--success-border`, title in
 *             `--success-text` (derived from brand-1),
 *   warning → faint destructive ramp (caution shares the danger hue),
 *   error   → stronger destructive ramp with all text in `text-error-text`
 *             (`--color-error-text`, an alias of `--foreground-destructive`).
 * A tone names its title colour in `--alert-title`, which `AlertTitle` paints;
 * warning and error leave it unset, so their titles inherit the root's text.
 * Every title colour holds 4.5:1 on its surface in both themes. Compose the
 * root with `AlertTitle` and `AlertDescription`. `error` is announced
 * assertively (`role="alert"`); the calmer tones use `role="status"`, and an
 * explicit `role` prop always wins.
 */
/** The surface and border of each status tone, shared with Badge. */
export const toneSurfaces = {
	info: 'border-info-border bg-info-surface',
	success: 'border-success-border bg-success-surface',
	warning: 'border-destructive/35 bg-destructive/7',
	error: 'border-destructive/55 bg-destructive/12',
} as const

const alertVariants = cva(
	'text-body-sm relative flex w-full flex-col gap-1 rounded-2xl border px-3 py-2',
	{
		variants: {
			tone: {
				info: [
					toneSurfaces.info,
					'text-foreground [--alert-title:var(--color-info-text)]',
				],
				success: [
					toneSurfaces.success,
					'text-foreground [--alert-title:var(--color-success-text)]',
				],
				warning: [toneSurfaces.warning, 'text-foreground'],
				error: [toneSurfaces.error, 'text-error-text'],
			},
		},
		defaultVariants: {
			tone: 'info',
		},
	},
)

export type AlertTone = NonNullable<VariantProps<typeof alertVariants>['tone']>

/** error is assertive; info/success/warning are polite status messages. */
const roleForTone: Record<AlertTone, 'alert' | 'status'> = {
	info: 'status',
	success: 'status',
	warning: 'status',
	error: 'alert',
}

function Alert({
	className,
	tone = 'info',
	role,
	...props
}: React.ComponentProps<'div'> & { tone?: AlertTone }) {
	return (
		<div
			data-slot="alert"
			data-tone={tone}
			role={role ?? roleForTone[tone]}
			className={cn(alertVariants({ tone }), className)}
			{...props}
		/>
	)
}

function AlertTitle({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot="alert-title"
			className={cn(
				'text-body-xs leading-none font-medium text-(--alert-title)',
				className,
			)}
			{...props}
		/>
	)
}

function AlertDescription({
	className,
	...props
}: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot="alert-description"
			className={cn('text-body-2xs [&_p]:leading-relaxed', className)}
			{...props}
		/>
	)
}

export { Alert, AlertTitle, AlertDescription, alertVariants }
